// Shared Clerk `appearance` factory. Re-skins Clerk's prebuilt <SignIn>/<SignUp> to the
// Champion Care identity (crimson #C8273A, cream/paper surfaces, Outfit body + headings, our
// radii) so the REAL Clerk auth UI looks like our product instead of stock Clerk.
//
// This is THEMING ONLY — it returns data for the `appearance` prop; it does not touch auth logic,
// providers, or the dev-login path. Each portal calls it with light per-portal overrides. We lean on
// Clerk `variables` for interactive states (button hover/active, links, input focus rings are derived
// from colorPrimary) and use `elements` for the structural touches variables can't express (display
// header, card shadow/border, pill button, input shape). We accept Clerk's card structure — re-skin,
// not rebuild (rebuilding markup would be Clerk Elements, out of scope).
import type { CSSProperties } from "react";

// Canonical brand tokens (kept in sync with @championcare/ui/styles.css light theme).
const INK = "#161616";
const INK_2 = "#2A2A2A";
const MUTED = "#7A716B";
const PAPER = "#FCFAF5";
const SURFACE = "#FFFFFF";
const LINE = "rgba(22,22,22,0.08)";
const LINE_2 = "rgba(22,22,22,0.14)";
const CREAM = "#FAF6F0";
const RED = "#C8273A";
const RED_DEEP = "#9F1E2E";
const FONT_BODY = "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif";
const FONT_DISPLAY = "Outfit, -apple-system, sans-serif";

// Localization overrides for Clerk — drops the "Sign in to {{applicationName}}" suffix so the card
// header reads just "Sign in" (the Clerk app is named "Champion Care Dev" in the dashboard; we don't
// want that string on the card). Pass to <ClerkProvider localization={clerkLocalization}>.
export const clerkLocalization: Record<string, unknown> = {
  signIn: { start: { title: "Sign in" } },
};

export interface ClerkThemeOptions {
  /** Primary / accent color (default Champion Care crimson). */
  accent?: string;
  /** Text/icon color ON the primary (accent) button. Default cream; the green portals pass dark
   *  ink so small button labels stay AA on the green fill (DESIGN.md §1.4). */
  onPrimary?: string;
  /** Danger/error color (default crimson red-deep). The green portals pass the danger red. */
  danger?: string;
  /** Base border-radius for inputs/buttons (default 12px). Card stays a bit rounder. */
  radius?: string;
  /** Card corner radius (default 18px — the consumer-rounded card). */
  cardRadius?: string;
}

/** Returns a Clerk `appearance` object themed to the Champion Care brand. */
export function clerkAppearance(opts: ClerkThemeOptions = {}): {
  variables: Record<string, string>;
  elements: Record<string, CSSProperties>;
} {
  const accent = opts.accent ?? RED;
  const radius = opts.radius ?? "12px";
  const cardRadius = opts.cardRadius ?? "18px";

  return {
    variables: {
      colorPrimary: accent, // drives primary button bg + hover/active, links, focus rings
      colorText: INK,
      colorTextSecondary: MUTED,
      colorTextOnPrimaryBackground: opts.onPrimary ?? CREAM,
      colorBackground: SURFACE,
      colorInputBackground: PAPER,
      colorInputText: INK,
      colorDanger: opts.danger ?? RED_DEEP,
      colorSuccess: "#2D8B5C",
      colorWarning: "#C8862A",
      colorNeutral: INK,
      borderRadius: radius,
      fontFamily: FONT_BODY,
      fontFamilyButtons: FONT_BODY,
      fontSize: "14.5px",
    },
    elements: {
      // Center the card horizontally regardless of how the host page wraps it (some portals' auth
      // wrappers center an auto-width box; a full-width rootBox would otherwise pin the card left).
      rootBox: { width: "100%", display: "flex", flexDirection: "column", alignItems: "center" },
      // The Clerk card — match our .pt-auth__card / .signin__card surface.
      card: {
        background: SURFACE,
        border: `1px solid ${LINE}`,
        borderRadius: cardRadius,
        boxShadow: "0 18px 50px -24px rgba(22,22,22,0.28)",
        padding: "clamp(26px, 4vw, 36px)",
      },
      // Headings — display font, like every portal's auth title.
      headerTitle: {
        fontFamily: FONT_DISPLAY,
        fontWeight: 500,
        fontSize: "26px",
        letterSpacing: "-0.01em",
        color: INK,
      },
      // Subtitle hidden — the card header reads just "Sign in" (see clerkLocalization).
      headerSubtitle: { display: "none" },
      // Social (Google) buttons — outlined to match our ghost buttons.
      socialButtonsBlockButton: {
        border: `1px solid ${LINE_2}`,
        borderRadius: radius,
        background: SURFACE,
      },
      socialButtonsBlockButtonText: { fontWeight: 600, color: INK },
      dividerLine: { background: LINE },
      dividerText: { color: MUTED, fontSize: "12px", letterSpacing: "0.08em" },
      // Inputs — match .pt-input (paper bg, rounded, our border).
      formFieldLabel: { fontWeight: 600, color: INK_2, fontSize: "13px" },
      formFieldInput: {
        border: `1px solid ${LINE_2}`,
        borderRadius: radius,
        background: PAPER,
        color: INK,
        fontSize: "14.5px",
        padding: "12px 14px",
      },
      // Primary button — crimson pill. Background/hover come from colorPrimary; we set shape + weight.
      formButtonPrimary: {
        borderRadius: "999px",
        fontWeight: 600,
        fontSize: "14px",
        textTransform: "none",
        letterSpacing: "0",
        padding: "11px 22px",
      },
      // Links + footer — crimson, like .pt-auth__link.
      footerActionLink: { color: accent, fontWeight: 600 },
      formResendCodeLink: { color: accent, fontWeight: 600 },
      identityPreviewEditButton: { color: accent },
      // OTP / verification fields.
      otpCodeFieldInput: { border: `1px solid ${LINE_2}`, borderRadius: radius, color: INK },
      // Center the brand logo if one is configured in the Clerk dashboard.
      logoBox: { justifyContent: "center" },
    },
  };
}

// DESIGN.md brand for the doctor/patient/command portals: green accent, dark-ink label on the green
// button (AA on the green fill), a real danger red. Marketing keeps the base clerkAppearance (crimson).
const CC_GREEN = "#0aa872";
const CC_INK = "#232323";
const CC_DANGER = "#DC2626";
export function portalClerkAppearance(opts: ClerkThemeOptions = {}) {
  return clerkAppearance({ accent: CC_GREEN, onPrimary: CC_INK, danger: CC_DANGER, ...opts });
}
